import { pvpRosterSize } from '@kenocyber/shared';

/**
 * **A Égide Espinhosa: um brilho na silhueta, e um feixe de volta.**
 *
 * ## Quem manda desenhar, e quem nunca manda
 *
 * Este módulo desenha uma REFLEXÃO que a autoridade confirmou. Ele não sabe o
 * que é um perfil, um socket, uma cópia, um nível ou uma fórmula, e não tem por
 * onde saber: as suas entradas são uma silhueta - a textura que a cena já usa
 * para o casco, com a sua transformação - e dois pontos. Não infere nada pelo
 * dano, pela textura do tiro nem pelo que a nave tem equipada.
 *
 * ## Porque o brilho usa a TEXTURA DO CASCO, e não uma forma inventada
 *
 * Porque o contrato pede duas coisas ao mesmo tempo: *«um brilho branco-neon
 * muito rápido percorre toda a silhueta»* e *«preserve a forma e a identidade da
 * nave»*. Qualquer forma desenhada aqui - um aro, um disco, um contorno
 * aproximado - falharia a segunda: seria a forma DELA, e não a da nave.
 *
 * A textura do casco resolve as duas por construção. `setTintFill` substitui a
 * COR de cada pixel pelo branco e **preserva o alpha**, portanto o que sai é a
 * silhueta exata da nave, sólida e branca. Não é um contorno parecido com a
 * nave: é a nave, acesa.
 *
 * E é também o que garante *«não cubra barra, marcador de equipe ou canhão»*:
 * o brilho ocupa exatamente os pixels do casco e nem um a mais, e a
 * profundidade que a cena lhe dá põe-no por baixo dos três.
 *
 * ## Duas camadas, e só duas
 *
 * 1. **núcleo** - a silhueta branca sólida, sobre o casco;
 * 2. **halo** - a mesma silhueta, um pouco maior, muito mais fraca, por baixo.
 *
 * O halo CRESCE enquanto apaga, e é isso que faz o brilho *percorrer* a silhueta
 * em vez de ficar parado a piscar. Não há partículas, não há fumo, não há
 * escudo, não há disco e não há nada que sobreviva aos 72 ms - o contrato proíbe
 * os cinco, e a maneira de não os ter é não os desenhar.
 *
 * ## E porque o feixe é uma TIRA, e não uma linha de `Graphics`
 *
 * Porque um `lineBetween` é um desenho por quadro: ele reconstrói geometria em
 * cada frame que vive, e uma reflexão pode acontecer seis vezes ao mesmo tempo.
 * Uma tira é uma textura criada UMA vez e esticada entre dois pontos - uma
 * imagem, uma rotação, uma escala - e não aloca nada enquanto corre.
 *
 * A tira tem o núcleo branco no meio e duas bandas cada vez mais fracas de cada
 * lado. É isso o *«núcleo branco e halo estreito»*, pintado na textura em vez de
 * empilhado em três objetos.
 */

/* -------------------------------------------------------------------------- */
/*  O tempo                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * **A vida de um brilho: 72 ms.**
 *
 * Dentro da banda 60–80 ms que o contrato aprova, e no meio dela de propósito: é
 * a distância máxima das duas pontas, portanto um ajuste de ritmo numa direção
 * ou noutra não sai da banda.
 *
 * Curto por decisão de arte, e a nota é a mesma do Ciclone: um efeito que se
 * expande devagar e fica a flutuar é uma AURA, e o contrato proíbe aura
 * explicitamente. Isto acontece e acaba.
 */
export const REFLECT_GLOW_DURATION_MS = 72;

/**
 * A vida de um feixe: a mesma, e é deliberado.
 *
 * Os dois nascem do MESMO facto - uma interceção confirmada - e um brilho que
 * durasse mais do que o feixe faria a nave continuar acesa depois de o golpe já
 * ter chegado ao outro lado. Um número partilhado é o que os mantém a acabar
 * juntos sem ninguém ter de os afinar um contra o outro.
 *
 * **Sem tempo de viagem mecânico**: o feixe aparece inteiro no primeiro quadro,
 * dos dois pontos ao mesmo tempo. Ele não representa um objeto a viajar - o dano
 * já foi aplicado - e animá-lo a percorrer a distância seria desenhar uma
 * viagem que a simulação não fez.
 */
export const REFLECT_BEAM_DURATION_MS = 72;

/** Quando o brilho atinge o máximo. Cedo: um relâmpago não sobe devagar. */
export const REFLECT_GLOW_PEAK_MS = 14;

/* -------------------------------------------------------------------------- */
/*  A forma                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Quanto o halo passa a silhueta ao acabar: **14 %**.
 *
 * Pequeno de propósito. O halo é *«discreto»* por contrato, e um que crescesse
 * o dobro deixaria de acompanhar a silhueta e passaria a ser uma mancha à volta
 * dela - que é a aura proibida, com outro nome.
 */
export const REFLECT_GLOW_HALO_SCALE = 1.14;

/** O alpha máximo do núcleo. Branco quase pleno, e nunca opaco. */
export const REFLECT_GLOW_CORE_ALPHA = 0.92;

/** O alpha máximo do halo. Uma fração do núcleo: ele é o eco, não o brilho. */
export const REFLECT_GLOW_HALO_ALPHA = 0.3;

/**
 * A espessura desenhada do feixe, em píxeis da arena: **9**.
 *
 * O núcleo ocupa uma fração disto - ver {@link BEAM_BANDS} - e o resto é halo.
 *
 * Passou de sete para nove no gate V28G.O.2, e os dois píxeis novos são TODOS
 * halo: o núcleo aceso encolheu de sete para menos de dois. O traço desenhado
 * ficou mais fino, não mais grosso - o que cresceu foi o brilho à volta dele.
 * *«Linha fina»* é uma decisão de leitura: um feixe grosso a atravessar a arena
 * lê-se como um laser contínuo, que é exatamente o que o `CLAUDE.md` proíbe
 * para toda a cadência do jogo e o que o §7 proíbe para este efeito.
 */
export const REFLECT_BEAM_THICKNESS_PX = 9;

/** O comprimento da textura da tira. Ela é ESTICADA; isto só decide a nitidez. */
const BEAM_TEXTURE_LENGTH = 64;

/** Branco-neon. A mesma cor do núcleo e do halo: um feixe não muda de cor. */
const NEON_WHITE = 0xffffff;

/**
 * As bandas da tira, do centro para fora: **alpha por linha de píxeis**.
 *
 * ## Porque o perfil mudou (gate V28G.O.2)
 *
 * O anterior tinha quatro bandas - sete linhas - com a primeira a alpha cheio e
 * uma queda curta. Sobre `ADD`, isso pinta uma barra: metade da altura do feixe
 * estava acima de 0,2 de alpha, e uma faixa branca larga e uniforme lê-se como
 * um traço desenhado, não como luz. O dono olhou para ele e disse-o.
 *
 * ## O que faz uma linha branca ler-se como NÉON
 *
 * Um filamento ESTREITO e muito aceso, e um halo LARGO que cai quase até nada.
 * É a razão entre os dois que dá a impressão de luz - não a quantidade de
 * branco. Um tubo de néon tem um traço fino e uma mancha à volta; uma barra
 * pintada tem só a barra.
 *
 * Dezassete linhas em vez de sete, e a distribuição inverteu-se:
 *
 * | parte | linhas | de 9 px | alpha |
 * | --- | ---: | ---: | --- |
 * | núcleo | 3 | ~1,6 px | 0,95 |
 * | halo | 14 | ~7,4 px | 0,5 a 0,009 |
 *
 * O núcleo é MENOR do que era em píxeis desenhados, e é isso «mais subtil»: o
 * que o olho lê como a linha encolheu para menos de dois píxeis. O halo é o
 * dobro do que era e cai por uma cauda longa, e é isso «mais néon».
 *
 * As duas primeiras bandas partilham o mesmo valor de propósito: são as linhas
 * `middle-1`, `middle` e `middle+1`, e é o que dá ao filamento uma espessura
 * que sobrevive à redução para nove píxeis. Uma só desapareceria em sub-píxel -
 * exatamente o defeito que apagou o verde do Coração Vital.
 *
 * Escritas como uma tabela e não como uma fórmula porque são uma decisão de
 * arte, e uma fórmula convidaria alguém a "melhorá-la" sem olhar para o
 * resultado.
 */
const BEAM_BANDS: readonly number[] = Object.freeze([
  0.95, 0.95, 0.5, 0.26, 0.14, 0.075, 0.04, 0.02, 0.009,
]);

/**
 * O alpha com que a tira NASCE: **0,8**, e nunca 1.
 *
 * Era 1. Um feixe a alpha pleno sobre `ADD` satura o que estiver por baixo dele
 * e passa de luz a corte branco - e o §7 pede um feixe *subtil*. Oitenta por
 * cento tira-lhe o peso sem lhe tirar a presença; o que se perde é a saturação,
 * e o que se perde de saturação ganha-se em leitura do que está por trás.
 */
export const REFLECT_BEAM_PEAK_ALPHA = 0.8;

/* -------------------------------------------------------------------------- */
/*  Os tetos, derivados                                                       */
/* -------------------------------------------------------------------------- */

/** Quantos cascos uma sala pode ter. Lido, nunca escrito. */
const HULLS = pvpRosterSize('team_3v3');

/**
 * **Quantos brilhos podem estar vivos: um por casco.**
 *
 * DERIVADO, e a derivação é uma decisão de desenho e não uma estimativa: um
 * casco tem **um** brilho, e uma segunda interceção dentro dos 72 ms REINICIA o
 * dele em vez de acender um segundo.
 *
 * ## Porque reiniciar em vez de empilhar
 *
 * Porque dois brilhos idênticos na mesma silhueta não são dois brilhos - são um
 * brilho mais claro. O jogador não conseguiria contá-los, e o custo seria dois
 * objetos e dois tweens a escrever no mesmo sítio. O que diz que houve duas
 * interceções é o número, que é impresso uma vez por cada.
 *
 * É o mesmo argumento de `healTextBudget`: quando o pior caso é "quantas naves
 * podem estar a fazer isto ao mesmo tempo", o teto é quantas naves existem.
 */
export const REFLECT_GLOW_POOL = HULLS;

/**
 * **Quantos feixes podem estar vivos: um por PAR ordenado de inimigos.**
 *
 * `6 x 5 = 30`. DERIVADO pela mesma lógica do brilho, com a unidade certa: um
 * feixe liga dois cascos, portanto o que o identifica é o par - e um segundo
 * feixe entre os mesmos dois dentro dos 72 ms reinicia o primeiro, porque
 * desenharia exatamente a mesma linha.
 *
 * `HULLS - 1` e não `HULLS`: um casco nunca se reflete a si próprio. A regra
 * está na tabela fechada do domínio; aqui ela só encolhe uma conta.
 */
export const REFLECT_BEAM_POOL = HULLS * (HULLS - 1);

/* -------------------------------------------------------------------------- */
/*  A cena, pela interface mínima                                             */
/* -------------------------------------------------------------------------- */

export interface ReflectFlashScene {
  readonly textures: { exists(key: string): boolean };
  readonly tweens: {
    add(config: Record<string, unknown>): { remove(): void };
  };
  make: {
    graphics(config?: unknown, addToScene?: boolean): ReflectFlashGraphics;
  };
  add: {
    image(x: number, y: number, key: string): ReflectFlashSprite;
  };
}

export interface ReflectFlashGraphics {
  fillStyle(color: number, alpha?: number): ReflectFlashGraphics;
  fillRect(x: number, y: number, width: number, height: number): ReflectFlashGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

export interface ReflectFlashSprite {
  x: number;
  y: number;
  rotation: number;
  alpha: number;
  setDepth(value: number): ReflectFlashSprite;
  setScale(x: number, y?: number): ReflectFlashSprite;
  setDisplaySize(width: number, height: number): ReflectFlashSprite;
  setVisible(value: boolean): ReflectFlashSprite;
  setTexture(key: string): ReflectFlashSprite;
  setTintFill(color: number): ReflectFlashSprite;
  setOrigin(x: number, y?: number): ReflectFlashSprite;
  setBlendMode(mode: string | number): ReflectFlashSprite;
  destroy(): void;
}

/** A textura da tira, criada uma vez por cena e reutilizada para sempre. */
export const REFLECT_BEAM_TEXTURE = 'reflect-beam';

/**
 * A tira: bandas horizontais, do núcleo para fora, simétricas.
 *
 * `textures.exists` antes de desenhar, porque uma cena reiniciada partilha o
 * gestor de texturas do jogo: regenerar seria pagar o mesmo desenho a cada
 * abertura de arena.
 */
function ensureBeamTexture(scene: ReflectFlashScene): void {
  if (scene.textures.exists(REFLECT_BEAM_TEXTURE)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const height = BEAM_BANDS.length * 2 - 1;
  const middle = BEAM_BANDS.length - 1;
  for (let band = 0; band < BEAM_BANDS.length; band += 1) {
    const alpha = BEAM_BANDS[band] as number;
    graphics.fillStyle(NEON_WHITE, alpha);
    /* A banda de cima e a de baixo. No centro (`band === 0`) são a mesma. */
    graphics.fillRect(0, middle - band, BEAM_TEXTURE_LENGTH, 1);
    if (band > 0) {
      graphics.fillRect(0, middle + band, BEAM_TEXTURE_LENGTH, 1);
    }
  }
  graphics.generateTexture(REFLECT_BEAM_TEXTURE, BEAM_TEXTURE_LENGTH, height);
  graphics.destroy();
}

/* -------------------------------------------------------------------------- */
/*  O que a cena entrega                                                      */
/* -------------------------------------------------------------------------- */

/** Uma silhueta a acender: a textura do casco, com a sua transformação. */
export interface ReflectGlowTarget {
  /** O casco que interceta. Identidade do slot, e nunca uma posição. */
  readonly id: string;
  /**
   * A textura DO CASCO, lida da imagem que a cena já desenha.
   *
   * É isto que faz o brilho ter a forma da nave em vez de uma forma inventada.
   * Este módulo não sabe o que a chave nomeia e não escolhe nenhuma: ele
   * recebe-a.
   */
  readonly textureKey: string;
  readonly x: number;
  readonly y: number;
  readonly rotation: number;
  /** O tamanho DESENHADO do casco, em píxeis da arena. A escala sai daqui. */
  readonly width: number;
  readonly height: number;
}

/** Um feixe: dois pontos, e a identidade do par que o produziu. */
export interface ReflectBeamShot {
  /** `refletor:destinatário`. Um segundo feixe do mesmo par reinicia este. */
  readonly id: string;
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
}

/* -------------------------------------------------------------------------- */
/*  O sistema                                                                 */
/* -------------------------------------------------------------------------- */

interface LiveGlow {
  readonly core: ReflectFlashSprite;
  readonly halo: ReflectFlashSprite;
  readonly tweens: { remove(): void }[];
  id: string;
  active: boolean;
}

interface LiveBeam {
  readonly strip: ReflectFlashSprite;
  readonly tweens: { remove(): void }[];
  id: string;
  active: boolean;
}

/**
 * **O sistema.** Um por cena, dono de tudo o que cria.
 *
 * Nada aqui é global e nada sobrevive a `destroy()`: os sprites são deste
 * sistema, os tweens são registados e removidos, e a textura da tira fica no
 * gestor da cena, que é quem a descarta quando a cena morre.
 */
export class ReflectFlashSystem {
  private readonly glows: LiveGlow[] = [];
  private readonly beams: LiveBeam[] = [];
  private disposed = false;

  constructor(
    private readonly scene: ReflectFlashScene,
    /** Por baixo do canhão, do marcador de equipa e da barra. A cena decide. */
    private readonly glowDepth: number,
    /** Por cima dos cascos e por baixo das barras. */
    private readonly beamDepth: number,
  ) {
    ensureBeamTexture(scene);
  }

  /**
   * Acende a silhueta de um casco.
   *
   * Idempotência é responsabilidade de quem entrega: a fila da ligação já
   * garante que um evento chega uma vez. O que ESTE método garante é que um
   * casco tem um brilho - uma segunda chamada dentro dos 72 ms reinicia o dele.
   */
  glow(target: ReflectGlowTarget): void {
    if (this.disposed) {
      return;
    }
    const live = this.takeGlow(target.id);
    live.id = target.id;
    live.active = true;

    for (const sprite of [live.core, live.halo]) {
      sprite.setTexture(target.textureKey);
      /*
       * **A COR substituída, o alpha preservado.**
       *
       * `setTintFill` e nunca `setTint`: o segundo MULTIPLICA, portanto um
       * branco sobre uma nave escura continuaria escuro. Este substitui, e o que
       * sobra é a silhueta exata, sólida e branca.
       */
      sprite.setTintFill(NEON_WHITE);
      /*
       * Aditivo, que é o que faz "neon" ser neon: a luz SOMA-SE ao que está por
       * baixo em vez de o tapar, portanto a nave continua a ler-se através do
       * brilho e a identidade dela sobrevive aos 72 ms.
       */
      sprite.setBlendMode('ADD');
      sprite.x = target.x;
      sprite.y = target.y;
      sprite.rotation = target.rotation;
      sprite.setDisplaySize(target.width, target.height);
      sprite.setVisible(true);
    }
    live.core.setDepth(this.glowDepth + 0.5);
    live.halo.setDepth(this.glowDepth - 0.5);

    /*
     * O NÚCLEO: sobe quase instantaneamente e apaga.
     *
     * O `alpha` parte de zero porque um flash que nasce no máximo pisca; catorze
     * milissegundos de subida são a diferença entre um relâmpago e um erro de
     * quadro.
     */
    live.core.alpha = 0;
    this.pushGlow(live, {
      targets: live.core,
      alpha: REFLECT_GLOW_CORE_ALPHA,
      duration: REFLECT_GLOW_PEAK_MS,
      ease: 'Quad.easeOut',
    });
    this.pushGlow(live, {
      targets: live.core,
      alpha: 0,
      delay: REFLECT_GLOW_PEAK_MS,
      duration: REFLECT_GLOW_DURATION_MS - REFLECT_GLOW_PEAK_MS,
      ease: 'Quad.easeIn',
    });

    /*
     * O HALO: nasce colado à silhueta e CRESCE 14 % enquanto apaga.
     *
     * É este crescimento que faz o brilho *percorrer* a silhueta em vez de ficar
     * parado: a luz sai de dentro para fora ao longo da forma da nave. Catorze
     * por cento é discreto por contrato - mais do que isso deixaria de
     * acompanhar a silhueta e passaria a ser a aura que o §7 proíbe.
     */
    live.halo.alpha = REFLECT_GLOW_HALO_ALPHA;
    this.pushGlow(live, {
      targets: live.halo,
      displayWidth: target.width * REFLECT_GLOW_HALO_SCALE,
      displayHeight: target.height * REFLECT_GLOW_HALO_SCALE,
      alpha: 0,
      duration: REFLECT_GLOW_DURATION_MS,
      ease: 'Quad.easeOut',
      /* O slot volta ao pool no MESMO instante em que o último pixel apaga. */
      onComplete: () => this.releaseGlow(live),
    });
  }

  /**
   * Desenha o feixe entre dois cascos.
   *
   * Inteiro no primeiro quadro, e nunca a viajar: o dano já foi aplicado, e
   * animar uma travessia seria desenhar uma viagem que a simulação não fez.
   */
  beam(shot: ReflectBeamShot): void {
    if (this.disposed) {
      return;
    }
    const dx = shot.toX - shot.fromX;
    const dy = shot.toY - shot.fromY;
    const length = Math.hypot(dx, dy);
    if (length <= 0) {
      /*
       * Dois cascos exatamente no mesmo ponto - geometricamente impossível entre
       * dois corpos sólidos. Sem comprimento não há feixe a desenhar, e um
       * comprimento inventado poria uma linha numa direção que ninguém decidiu.
       */
      return;
    }
    const live = this.takeBeam(shot.id);
    live.id = shot.id;
    live.active = true;

    const strip = live.strip;
    /*
     * A ORIGEM na ponta esquerda e no meio da altura: a tira cresce a partir do
     * refletor na direção do destinatário, e roda em torno do ponto de partida.
     */
    strip.setOrigin(0, 0.5);
    strip.setBlendMode('ADD');
    strip.x = shot.fromX;
    strip.y = shot.fromY;
    strip.rotation = Math.atan2(dy, dx);
    strip.setDisplaySize(length, REFLECT_BEAM_THICKNESS_PX);
    strip.setVisible(true);
    strip.alpha = REFLECT_BEAM_PEAK_ALPHA;

    /*
     * Uma queda só, e sem crescimento.
     *
     * O feixe não engorda, não encolhe e não se move: ele está lá e apaga. Uma
     * linha que se expandisse leria como uma onda de choque, que é outro efeito
     * e não este.
     */
    this.pushBeam(live, {
      targets: strip,
      alpha: 0,
      duration: REFLECT_BEAM_DURATION_MS,
      ease: 'Quad.easeIn',
      onComplete: () => this.releaseBeam(live),
    });
  }

  /** Quantos brilhos estão vivos agora. Para as provas e para as medições. */
  liveGlowCount(): number {
    return this.glows.filter((entry) => entry.active).length;
  }

  /** Quantos feixes estão vivos agora. */
  liveBeamCount(): number {
    return this.beams.filter((entry) => entry.active).length;
  }

  /** Quantos objetos o sistema alocou ao todo. Nunca deve crescer sem uso. */
  pooledCount(): number {
    return this.glows.length + this.beams.length;
  }

  /**
   * Apaga tudo o que está no ecrã, sem largar os objetos.
   *
   * Para uma CORREÇÃO, uma reconexão ou uma substituição de mundo: o que estava
   * a ser desenhado pertencia a um estado que já não existe.
   */
  clear(): void {
    for (const live of this.glows) {
      this.releaseGlow(live);
    }
    for (const live of this.beams) {
      this.releaseBeam(live);
    }
  }

  /**
   * Larga tudo. Depois disto o sistema não desenha mais nada.
   *
   * Os tweens são removidos ANTES dos sprites: um tween cujo alvo foi destruído
   * continua a correr e a escrever num objeto morto.
   */
  destroy(): void {
    this.disposed = true;
    for (const live of this.glows) {
      for (const tween of live.tweens) {
        tween.remove();
      }
      live.tweens.length = 0;
      live.core.destroy();
      live.halo.destroy();
    }
    this.glows.length = 0;
    for (const live of this.beams) {
      for (const tween of live.tweens) {
        tween.remove();
      }
      live.tweens.length = 0;
      live.strip.destroy();
    }
    this.beams.length = 0;
  }

  private pushGlow(live: LiveGlow, config: Record<string, unknown>): void {
    live.tweens.push(this.scene.tweens.add(config));
  }

  private pushBeam(live: LiveBeam, config: Record<string, unknown>): void {
    live.tweens.push(this.scene.tweens.add(config));
  }

  private releaseGlow(live: LiveGlow): void {
    for (const tween of live.tweens) {
      tween.remove();
    }
    live.tweens.length = 0;
    live.active = false;
    live.core.setVisible(false);
    live.halo.setVisible(false);
  }

  private releaseBeam(live: LiveBeam): void {
    for (const tween of live.tweens) {
      tween.remove();
    }
    live.tweens.length = 0;
    live.active = false;
    live.strip.setVisible(false);
  }

  /**
   * O slot deste CASCO, e é sempre o mesmo.
   *
   * A identidade vem primeiro: um casco que já está a brilhar reinicia o seu
   * brilho em vez de acender um segundo. Só depois se procura um livre, e só
   * depois se cria - e o teto nunca é alcançado por construção, porque há
   * exatamente um slot por casco possível.
   */
  private takeGlow(id: string): LiveGlow {
    const mine = this.glows.find((entry) => entry.active && entry.id === id);
    if (mine) {
      this.releaseGlow(mine);
      return mine;
    }
    const free = this.glows.find((entry) => !entry.active);
    if (free) {
      return free;
    }
    const created: LiveGlow = {
      core: this.blankSprite(),
      halo: this.blankSprite(),
      tweens: [],
      id,
      active: false,
    };
    this.glows.push(created);
    return created;
  }

  /** O slot deste PAR, pela mesma regra exata. */
  private takeBeam(id: string): LiveBeam {
    const mine = this.beams.find((entry) => entry.active && entry.id === id);
    if (mine) {
      this.releaseBeam(mine);
      return mine;
    }
    const free = this.beams.find((entry) => !entry.active);
    if (free) {
      return free;
    }
    const strip = this.scene.add.image(0, 0, REFLECT_BEAM_TEXTURE);
    strip.setDepth(this.beamDepth).setVisible(false);
    const created: LiveBeam = { strip, tweens: [], id, active: false };
    this.beams.push(created);
    return created;
  }

  /**
   * Um sprite sem textura própria: ela é atribuída a cada brilho.
   *
   * Nasce com a da tira porque `add.image` precisa de uma chave que exista, e
   * ela é substituída pela do casco na primeira utilização. Nunca é DESENHADA
   * assim: o sprite nasce invisível.
   */
  private blankSprite(): ReflectFlashSprite {
    const sprite = this.scene.add.image(0, 0, REFLECT_BEAM_TEXTURE);
    sprite.setDepth(this.glowDepth).setVisible(false);
    return sprite;
  }
}
